import React from 'react'
import {
  DollarSign,
  PieChart,
  RefreshCw,
  FileSpreadsheet,
  Check,
  Shield,
} from 'lucide-react'

interface ExecutiveGovernanceProps {
  brandName?: string | null
}

export const ExecutiveGovernance: React.FC<ExecutiveGovernanceProps> = ({
  brandName,
}) => {
  const pillars = [
    {
      icon: DollarSign,
      number: '01',
      title: 'Gestão Ativa de Contas a Pagar e Receber',
      subtitle: 'Controle rigoroso de prazos e fluxo',
      description:
        'Auditoria prévia de documentos fiscais, agendamentos bancários com aprovação em dois níveis e esteira ativa de cobrança para mitigação de inadimplência.',
      highlights: [
        'Aprovação hierárquica por alçada',
        'Controle diário de vencimentos e multas',
        'Conciliação automatizada de boletos e PIX',
      ],
    },
    {
      icon: PieChart,
      number: '02',
      title: 'DRE Gerencial & Apuração de EBITDA',
      subtitle: 'Inteligência e margens corporativas',
      description:
        'Estruturação contábil por centro de custos, demonstrativo de resultados do exercício em tempo real e relatórios de inteligência financeira para tomadores de decisão.',
      highlights: [
        'Margem de contribuição por unidade',
        'EBITDA e Lucro Líquido gerencial',
        'Relatórios executivos para conselho',
      ],
    },
    {
      icon: RefreshCw,
      number: '03',
      title: 'Conciliação Bancária Diária & Auditoria',
      subtitle: 'Fechamento contínuo em 24 horas',
      description:
        'Processamento diário de extratos bancários, cruzamento transacional via Open Finance/OFX e resolução imediata de divergências contábeis.',
      highlights: [
        'Auditoria de tarifas e estornos',
        'Sincronização multi-bancos contínua',
        'Fechamento contábil sem acúmulo no fim do mês',
      ],
    },
    {
      icon: FileSpreadsheet,
      number: '04',
      title: 'Planejamento Tributário & Orçamentário',
      subtitle: 'Previsibilidade e segurança fiscal',
      description:
        'Projeções de fluxo de caixa futuro para 30, 60 e 90 dias, simulação de cenários orçamentários e governança fiscal estruturada com especialistas.',
      highlights: [
        'Projeções de liquidez futura',
        'Acompanhamento Orçado vs Realizado',
        'Conformidade e governança fiscal',
      ],
    },
  ]

  return (
    <section
      id="governanca"
      className="relative py-20 md:py-28 px-6 lg:px-10 border-b border-border overflow-hidden bg-surface-2 text-foreground text-left"
    >
      {/* Selo Editorial da Seção */}
      <div className="absolute top-14 right-6 lg:right-10 text-[11px] font-display font-semibold tracking-[0.35em] text-bronze uppercase">
        02 — GOVERNANÇA
      </div>

      <div className="max-w-[1360px] mx-auto">
        {/* Cabeçalho da Seção */}
        <div className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-border-strong bg-primary-light/70 text-bronze text-[10px] font-semibold tracking-[0.28em] uppercase mb-6">
            <Shield className="w-3.5 h-3.5" />
            <span>Metodologia & Pilares</span>
          </div>

          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[3rem] leading-[0.98] tracking-[-0.03em] text-foreground mb-6">
            Pilares de Governança Financeira & Controle Estratégico
          </h2>

          <p className="text-lg text-text-3 leading-relaxed">
            A metodologia de BPO Estratégico da {brandName || 'nossa consultoria'} substitui o caos operacional por governança corporativa, precisão matemática e visão preditiva.
          </p>
        </div>

        {/* Grid de 4 Pilares no Padrão 2x2 Seven UI */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {pillars.map((pillar, idx) => {
            const Icon = pillar.icon
            return (
              <div
                key={idx}
                className="p-8 sm:p-10 bg-surface border border-border hover:border-border-strong transition-all flex flex-col justify-between group relative"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="p-3 border border-border bg-surface-2 text-bronze group-hover:border-border-strong transition-colors">
                      <Icon className="w-6 h-6 text-bronze" strokeWidth={1.6} />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-px bg-border-strong group-hover:bg-bronze transition-colors" />
                      <span className="text-xl font-display font-bold font-mono text-bronze tabular-nums">
                        {pillar.number}
                      </span>
                    </div>
                  </div>

                  <h3 className="font-display font-bold text-xl sm:text-2xl text-foreground mb-2 group-hover:text-bronze transition-colors">
                    {pillar.title}
                  </h3>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-bronze mb-4">
                    {pillar.subtitle}
                  </p>
                  <p className="text-sm text-text-3 leading-relaxed mb-8">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-6 border-t border-border">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-text-4 mb-3">
                    Entregáveis Inclusos:
                  </div>
                  <ul className="space-y-2.5">
                    {pillar.highlights.map((item, hIdx) => (
                      <li
                        key={hIdx}
                        className="flex items-center gap-2.5 text-xs text-foreground font-medium"
                      >
                        <Check className="w-3.5 h-3.5 text-bronze shrink-0" strokeWidth={2.2} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
